import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { DeviceHealthScore } from "@/utils/deviceHealth";
import { 
  Shield, 
  RefreshCw, 
  Battery, 
  Monitor, 
  Grid3X3, 
  TrendingUp,
  AlertTriangle,
  CheckCircle
} from "lucide-react";

interface HealthInsightsPanelProps {
  healthScore: DeviceHealthScore;
  onActionClick?: (action: string) => void;
}

export const HealthInsightsPanel = ({ 
  healthScore, 
  onActionClick 
}: HealthInsightsPanelProps) => {
  // Get all recommendations from metrics
  const allRecommendations = healthScore.metrics
    .filter(metric => metric.recommendations && metric.recommendations.length > 0)
    .flatMap(metric => 
      metric.recommendations!.map(rec => ({
        metric: metric.name,
        recommendation: rec,
        priority: metric.status === "poor" ? "high" : metric.status === "fair" ? "medium" : "low"
      }))
    );
  
  // Get critical issues (poor status)
  const criticalIssues = healthScore.metrics.filter(metric => metric.status === "poor");
  
  // Get areas that need attention (fair status)
  const attentionAreas = healthScore.metrics.filter(metric => metric.status === "fair");
  
  // Generate quick actions based on issues
  const quickActions = [
    ...(criticalIssues.some(m => m.name === "OS Compliance") ? [
      { icon: Monitor, label: "Update OS", action: "update-os" }
    ] : []),
    ...(criticalIssues.some(m => m.name === "Security Patches") ? [
      { icon: Shield, label: "Security Scan", action: "security-scan" }
    ] : []),
    ...(criticalIssues.some(m => m.name === "Battery Health") ? [
      { icon: Battery, label: "Battery Check", action: "battery-check" }
    ] : []),
    { icon: RefreshCw, label: "Refresh Data", action: "refresh" },
    { icon: Grid3X3, label: "App Inventory", action: "app-inventory" }
  ];

  return (
    <div className="space-y-4">
      {/* Health Overview */}
      <GlassCard variant="default" hover={false}>
        <GlassCardHeader>
          <GlassCardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Health Overview
          </GlassCardTitle>
        </GlassCardHeader>
        <GlassCardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-green-600">
                {healthScore.metrics.filter(m => m.status === "excellent").length}
              </div>
              <div className="text-sm text-muted-foreground">Excellent</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-600">
                {attentionAreas.length}
              </div>
              <div className="text-sm text-muted-foreground">Need Attention</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">
                {criticalIssues.length}
              </div>
              <div className="text-sm text-muted-foreground">Critical Issues</div>
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Critical Issues */}
      {criticalIssues.length > 0 && (
        <GlassCard variant="default" hover={false}>
          <GlassCardHeader>
            <GlassCardTitle className="text-base flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-4 w-4" />
              Critical Issues
            </GlassCardTitle>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="space-y-2">
              {criticalIssues.map((metric, index) => (
                <Alert key={index} className="border-red-200">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>{metric.name}:</strong> {metric.description}
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          </GlassCardContent>
        </GlassCard>
      )}

      {/* Quick Actions */}
      <GlassCard variant="default" hover={false}>
        <GlassCardHeader>
          <GlassCardTitle className="text-base">Quick Actions</GlassCardTitle>
        </GlassCardHeader>
        <GlassCardContent>
          <div className="grid grid-cols-2 gap-2">
            {quickActions.map((action, index) => (
              <Button
                key={index}
                variant="outline"
                size="sm"
                className="justify-start gap-2"
                onClick={() => onActionClick?.(action.action)}
              >
                <action.icon className="h-4 w-4" />
                {action.label}
              </Button>
            ))}
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* Recommendations */}
      {allRecommendations.length > 0 && (
        <GlassCard variant="default" hover={false}>
          <GlassCardHeader>
            <GlassCardTitle className="text-base flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Recommendations
            </GlassCardTitle>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="space-y-3">
              {allRecommendations.map((item, index) => (
                <div key={index} className="flex gap-3">
                  <Badge 
                    variant={
                      item.priority === "high" ? "destructive" : 
                      item.priority === "medium" ? "outline" : "secondary"
                    }
                    className="text-xs"
                  >
                    {item.priority}
                  </Badge>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{item.metric}</p>
                    <p className="text-sm text-muted-foreground">{item.recommendation}</p>
                  </div>
                </div>
              ))}
            </div>
          </GlassCardContent>
        </GlassCard>
      )}
    </div>
  );
};